/**
 * The filter a person puts on one record's entry list, and the SQL for it.
 *
 * This module BUILDS SQL and never runs it, exactly as `insight-query.ts` and
 * `insight-views.ts` do, and for the same reason: expo-sqlite cannot load under
 * bun, so a module that imports `db.ts` cannot be unit tested. `entry-repo.ts`
 * is where these statements meet a connection.
 *
 * **This is not `InsightFilter`, and the difference is not cosmetic.** A list
 * filter has to say "Cash or UPI", and no operator in `insight-query.ts` holds
 * two values. Adding one there would put an array into a card's stored spec,
 * and `isFilter` in `insight-layout.ts` rejects an array — one bad condition
 * drops the WHOLE card, so a card written by a newer build would silently
 * vanish on an older phone. The two also have different lifetimes: a card is a
 * persisted artifact whose id is a hash of its spec, and a list filter is
 * session state that clears when the screen is left. Different lifetimes,
 * different types. Nothing here is ever written to `insight_layout`.
 *
 * **One predicate builder, two tables.** The list reads `entry_mirror` with
 * inline `json_extract`, because the per-record view exposes no `values_json`,
 * `created_at` or `version` and the list needs all three. The insight cards
 * read `v_<record>`, where the same field is a `f_<fieldId>` column. Both go
 * through `fieldPredicate` with a different `FieldColumns`, so the two can
 * never drift — and a row that passes the list filter cannot fail the card
 * filter.
 *
 * Identifiers are interpolated; values are always bound. SQLite has no
 * parameter for a column name or a JSON path, so those go through the same
 * `quoteIdent` and `quoteLiteral` that `insight-views.ts` already tests against
 * hostile input.
 */
import {
  answersMoney,
  asksDateCondition,
  dateConditionSql,
  isDateConditionOp,
  isWorkedOutNumber,
  parseFigure,
  VALUE_TYPES,
  type DateConditionOp,
  type RecordSchemaField,
  type RecordSchemaResponse,
} from '@alluzo/shared';

import {
  previousWindowFor,
  windowFor,
  type DayWindow,
  type RuntimeCut,
} from '@/lib/insight-query';
import { columnNameFor, jsonPathFor, quoteIdent, quoteLiteral } from '@/lib/insight-views';
import { fold } from '@/lib/search-present';
import { entryTextParams, entryTextSql, isSearchable } from '@/lib/search-query';

/* ------------------------------------------------------------------ model */

/**
 * What a filter asks of one field.
 *
 *   any_of / none_of  the stored value against a set. A reference holds an
 *                     entry id and a select holds an option id, so this is how
 *                     "the payment mode is Cash or UPI" is said with no word
 *                     for either.
 *   all_of            a multi select holding every one of the set: "Hair and
 *                     Nails were both done". Over a single value it can only
 *                     hold for a set of one.
 *   gte / lte         a number, compared as a number. A range is two of these.
 *   set / unset       whether the field was answered at all.
 *
 * `none_of` is deliberately TRUE for an entry with no value: an entry with no
 * payment mode is not on credit. That is the same choice `is_not` documents in
 * `insight-query.ts`, and for the same reason — plain `NOT IN` answers NULL
 * there and drops the row, which reads as "we could not tell".
 */
export type EntryFieldFilter =
  | {
      readonly fieldId: string;
      readonly op: 'any_of' | 'none_of' | 'all_of';
      readonly values: readonly (string | number)[];
    }
  | { readonly fieldId: string; readonly op: 'gte' | 'lte'; readonly value: number }
  | { readonly fieldId: string; readonly op: 'set' | 'unset' }
  /**
   * A date condition (F21 of docs/plans/field-relations.md) on any stored
   * date, a date total included: before today, in the next 7 days, on or
   * after a date. `value` is the count of days or the date the condition
   * takes, absent on the three about today. Worked out with the device's day.
   */
  | { readonly fieldId: string; readonly op: DateConditionOp; readonly value?: number | string };

/** The days the list is cut to. A preset is resolved against the device's own today. */
export type EntryWindow =
  | {
      readonly kind: 'preset';
      readonly preset: 'today' | 'yesterday' | 'week' | 'month' | 'last_month';
    }
  | { readonly kind: 'range'; readonly from: string; readonly to: string };

/**
 * The whole filter. OR within one field, AND across fields.
 *
 * Two levels and never deeper — the same shape `link-condition.tsx` settled
 * on. It covers every question a shop actually asks, and a bracket inside a
 * bracket cannot be read on a phone.
 */
export type EntryFilterSet = {
  readonly window: EntryWindow | null;
  readonly fields: readonly EntryFieldFilter[];
};

export const EMPTY_FILTER: EntryFilterSet = { window: null, fields: [] };

export const isFiltering = (set: EntryFilterSet): boolean =>
  set.window !== null || set.fields.length > 0;

/**
 * How many filters are on, as the badge and the chip row count them.
 *
 * One per FIELD, not one per condition: "at least 500 and at most 2,000" is one
 * range a person set once, and a badge reading 2 for it would be counting our
 * internals at them. The window counts as one more.
 */
export const activeCount = (set: EntryFilterSet): number => {
  const fields = new Set(set.fields.map((filter) => filter.fieldId));
  return fields.size + (set.window === null ? 0 : 1);
};

/** Every field this filter asks about, in the order it first asks. */
export const fieldIdsOf = (set: EntryFilterSet): readonly string[] => {
  const seen: string[] = [];
  for (const filter of set.fields) {
    if (!seen.includes(filter.fieldId)) seen.push(filter.fieldId);
  }
  return seen;
};

/** What one field is being asked. Empty when the field is not in the filter. */
export const filtersForField = (
  set: EntryFilterSet,
  fieldId: string,
): readonly EntryFieldFilter[] => set.fields.filter((filter) => filter.fieldId === fieldId);

/**
 * Every string value the filter carries.
 *
 * A reference value is an entry id and a select value is an option id, so the
 * chips and the card labels need names for them. One batch through
 * `titlesForEntryIds` rather than one lookup per chip.
 */
export const stringValuesOf = (set: EntryFilterSet): readonly string[] => {
  const found = new Set<string>();
  for (const filter of set.fields) {
    if (filter.op !== 'any_of' && filter.op !== 'none_of' && filter.op !== 'all_of') continue;
    for (const value of filter.values) {
      if (typeof value === 'string') found.add(value);
    }
  }
  return [...found];
};

/* ----------------------------------------------------------------- edits */

/** Every filter this set carries EXCEPT the ones on this field. */
const without = (set: EntryFilterSet, fieldId: string): readonly EntryFieldFilter[] =>
  set.fields.filter((filter) => filter.fieldId !== fieldId);

export const withWindow = (set: EntryFilterSet, window: EntryWindow | null): EntryFilterSet => ({
  ...set,
  window,
});

export const withoutField = (set: EntryFilterSet, fieldId: string): EntryFilterSet => ({
  ...set,
  fields: without(set, fieldId),
});

/**
 * Set what a choose-many field is asked, replacing whatever it held.
 *
 * `includeUnset` is the "No item" row of the value pane. It is a second
 * condition on the same field, and the two are ORed: "Cash, UPI, or nothing at
 * all" is one question.
 */
export const withFieldChoice = (
  set: EntryFilterSet,
  fieldId: string,
  choice: {
    readonly values: readonly (string | number)[];
    readonly includeUnset: boolean;
    /** How a multi select is asked: one of the values, or all of them. `any_of` when absent. */
    readonly match?: ChoiceMatch;
  } | null,
): EntryFilterSet => {
  const kept = without(set, fieldId);
  if (choice === null) return { ...set, fields: kept };
  const added: EntryFieldFilter[] = [];
  if (choice.values.length > 0) {
    added.push({ fieldId, op: choice.match ?? 'any_of', values: [...choice.values] });
  }
  if (choice.includeUnset) added.push({ fieldId, op: 'unset' });
  return { ...set, fields: [...kept, ...added] };
};

/** "Any of" or "all of": how a multi select is asked about the values ticked. */
export type ChoiceMatch = 'any_of' | 'all_of';

/** How this field's ticked values are asked today. `any_of` when nothing is ticked. */
export const choiceMatchOf = (set: EntryFilterSet, fieldId: string): ChoiceMatch =>
  filtersForField(set, fieldId).some((filter) => filter.op === 'all_of') ? 'all_of' : 'any_of';

/** The values ticked on one field, whichever way they are asked. */
export const chosenValuesOf = (set: EntryFilterSet, fieldId: string): readonly (string | number)[] => {
  for (const filter of filtersForField(set, fieldId)) {
    if (filter.op === 'any_of' || filter.op === 'all_of') return filter.values;
  }
  return [];
};

/** Ask the same ticks the other way. Nothing changes on a field with nothing ticked. */
export const withChoiceMatch = (set: EntryFilterSet, fieldId: string, match: ChoiceMatch): EntryFilterSet => ({
  ...set,
  fields: set.fields.map((filter) =>
    filter.fieldId === fieldId && (filter.op === 'any_of' || filter.op === 'all_of')
      ? { ...filter, op: match }
      : filter,
  ),
});

/** Set a range on a numeric field. Either side may be open. */
export const withFieldRange = (
  set: EntryFilterSet,
  fieldId: string,
  range: { readonly min: number | null; readonly max: number | null },
): EntryFilterSet => {
  const kept = without(set, fieldId);
  const added: EntryFieldFilter[] = [];
  if (range.min !== null) added.push({ fieldId, op: 'gte', value: range.min });
  if (range.max !== null) added.push({ fieldId, op: 'lte', value: range.max });
  return { ...set, fields: [...kept, ...added] };
};

/** Ask a date field one date condition (F21), replacing what it held. Null takes the question off. */
export const withFieldDate = (
  set: EntryFilterSet,
  fieldId: string,
  condition: { readonly op: DateConditionOp; readonly value?: number | string } | null,
): EntryFilterSet => {
  const kept = without(set, fieldId);
  return { ...set, fields: condition === null ? kept : [...kept, { fieldId, ...condition }] };
};

/**
 * The fields a date condition may ask about: every stored date, a worked-out
 * one and a date total included, since each keeps its day in its slot. The
 * record's own date is here too: the Days pills cut by it, and a condition
 * asks it something the pills cannot ("before today").
 */
export const dateFilterableFieldsOf = (schema: RecordSchemaResponse): readonly RecordSchemaField[] =>
  schema.fields
    .filter((field) => asksDateCondition(field))
    .sort((a, b) => a.ord - b.ord);

/** Ask an attachment field whether it was answered. Null takes the question off. */
export const withFieldPresence = (
  set: EntryFilterSet,
  fieldId: string,
  op: 'set' | 'unset' | null,
): EntryFilterSet => {
  const kept = without(set, fieldId);
  return { ...set, fields: op === null ? kept : [...kept, { fieldId, op }] };
};

/* ------------------------------------------------------------ the window */

/**
 * The days a filter covers, or null for all time.
 *
 * The presets are `windowFor` and `previousWindowFor` from `insight-query.ts`
 * and nothing else. That is the whole point of reusing them: a pill called
 * "This month" and an insight card scoped to the month mean the same days, and
 * neither can drift from the other.
 */
export const windowOf = (set: EntryFilterSet, today: string): DayWindow | null => {
  const window = set.window;
  if (window === null) return null;
  if (window.kind === 'range') return { from: window.from, to: window.to };
  switch (window.preset) {
    case 'today':
      return windowFor('day', today);
    case 'yesterday':
      return previousWindowFor('day', today);
    case 'week':
      return windowFor('week', today);
    case 'month':
      return windowFor('month', today);
    case 'last_month':
      return previousWindowFor('month', today);
  }
};

/* -------------------------------------------------------------- the gate */

/**
 * The controls phase 1 serves.
 *
 * `contains` (text, long_text, phone) and `within` (location) are deliberately
 * absent. Nobody's note matches exactly, which is why the registry gives text
 * `contains` in the first place, and contains is what search is for.
 */
const PHASE_ONE_CONTROLS: readonly string[] = [
  'choose_many',
  'range',
  'yes_no',
  'present_absent',
];

/**
 * The fields this record can be filtered by.
 *
 * Four gates, and only the last one is ours. The other three are facts about
 * where a value lives:
 *
 *   computed   a lookup stores nothing, so it is never in `values_json`, and
 *              a range on one would match nothing at all, silently, which is
 *              worse than not offering it. A formula or a rollup whose answer
 *              is a number, an amount or a quantity IS offered, as a range
 *              (6.4 of docs/plans/workbook-map.md: Customers whose Pending is
 *              at least 1, Roller jobs whose Balance is): it keeps its answer
 *              in its slot, and `fromStorage` serves that in the row's values.
 *              One that answers words, a yes or a no, or a date is not here;
 *              a date is `dateFilterableFieldsOf`'s.
 *   link       a field kept in `data.entry_link` is not in `values_json`, with
 *              one exception: a multi select arrives there as the array of
 *              rows it holds, `usedValuesFor` lists each member, and the
 *              compiler asks it "any of" or "all of" (Decision 8 of
 *              `dependent-choices-and-multi-select.md`).
 *   date       the Days pills own the time axis. A second date field cannot
 *              ride `gte`/`lte`, which carry a number.
 *
 * The last gate is the frozen registry's own `filter_control` column, so which
 * control a type gets is not a decision this file makes.
 *
 * Sorted by `ord` alone, which is the entry form's own order — the order the
 * shop dragged its fields into. `entry-form.tsx` explains why nothing is
 * layered on top of it.
 */
export const filterableFieldsOf = (
  schema: RecordSchemaResponse,
): readonly RecordSchemaField[] =>
  schema.fields
    .filter((field) => {
      if (field.type === 'formula' || field.type === 'rollup') return isWorkedOutNumber(field);
      if (field.storage === 'computed') return false;
      if (field.storage === 'link' && field.type !== 'multi_select') return false;
      if (field.type === 'date' || field.type === 'datetime') return false;
      return PHASE_ONE_CONTROLS.includes(VALUE_TYPES[field.type].filter_control);
    })
    .sort((a, b) => a.ord - b.ord);

/**
 * The fields whose stored value is an array, which every compile below has to
 * be told: a filter carries a field id and nothing else.
 */
export const arrayFieldsOf = (schema: RecordSchemaResponse | null): ReadonlySet<string> =>
  new Set((schema?.fields ?? []).filter((field) => field.type === 'multi_select').map((field) => field.id));

/**
 * Whether a field's figure is an amount, drawn in the record's currency: a
 * money field, or a formula or a total that works one out (Pending on
 * Customers, 6.4 of docs/plans/workbook-map.md).
 */
export const holdsMoney = (field: Pick<RecordSchemaField, 'type' | 'spec'>): boolean => answersMoney(field);

/** Which pane a field's row opens. Read off the registry, never off the type. */
export type FilterControl = 'choose_many' | 'range' | 'yes_no' | 'present_absent';

export const controlFor = (field: RecordSchemaField): FilterControl =>
  VALUE_TYPES[field.type].filter_control as FilterControl;

/**
 * More recorded values than this and a field is a long list.
 *
 * Eight rows is about what fits above the footer without scrolling past the
 * point of a glance. On the phone a long field says how many values it holds
 * under its name in the sheet's field list; its values pane carries a search
 * box whatever its length (`valueMatches`). The web's values column draws its
 * search box only past this many.
 */
export const PANE_VALUE_LIMIT = 8;

/* ---------------------------------------------------- finding a value */

/**
 * Whether a value's word answers what was typed into the values pane's box.
 *
 * Case and accents do not count — `fold`, the fold global search applies, so
 * `chavan` finds Chàvan here as it does there. Word by word, and each typed
 * word anywhere in the value, so "kumar ram" still finds Ram Kumar. Nothing
 * typed, or only spaces, answers every value.
 */
export const valueMatches = (label: string, typed: string): boolean => {
  const words = fold(typed).trim().split(/\s+/).filter((word) => word !== '');
  if (words.length === 0) return true;
  const drawn = fold(label);
  return words.every((word) => drawn.includes(word));
};

/** The rows whose word answers what was typed, in the order they came. */
export const matchingValues = <Row extends { readonly label: string }>(
  rows: readonly Row[],
  typed: string,
): readonly Row[] => (typed.trim() === '' ? rows : rows.filter((row) => valueMatches(row.label, typed)));

/* --------------------------------------------------- a typed range */

/**
 * One end of a range, as typed: nothing (that side stays open), a figure, or
 * something that is not one.
 */
export type TypedBound =
  | { readonly kind: 'open' }
  | { readonly kind: 'figure'; readonly value: number }
  | { readonly kind: 'not_a_figure' };

/**
 * Read what was typed into one end of a range.
 *
 * `parseFigure` from the importer reads it, so grouping commas, a minus and a
 * currency mark are taken as a figure the way a pasted cell is. A point typed
 * last is a figure still being typed — "500." is 500 — and a debit or credit
 * mark is not a figure here: a range compares the number as stored.
 */
export const readBound = (typed: string): TypedBound => {
  const text = typed.trim();
  if (text === '') return { kind: 'open' };
  const read = parseFigure(text.endsWith('.') ? text.slice(0, -1) : text, true);
  if (read === null || read.code !== null) return { kind: 'not_a_figure' };
  return { kind: 'figure', value: read.value };
};

/** Why two typed ends do not make a range. */
export type RangeProblem = 'from_not_a_figure' | 'to_not_a_figure' | 'crossed';

/**
 * The range two typed ends make, or why they make none.
 *
 * An empty end leaves that side open, so either end alone is a range. From may
 * equal To — "exactly 500" is a range of one figure — and may not be more than
 * it: a floor above the ceiling matches nothing, and the pane says so rather
 * than handing the list an empty cut. The From end is read first, so with both
 * ends wrong it is the one named.
 */
export const readTypedRange = (
  from: string,
  to: string,
):
  | { readonly ok: true; readonly min: number | null; readonly max: number | null }
  | { readonly ok: false; readonly problem: RangeProblem } => {
  const low = readBound(from);
  if (low.kind === 'not_a_figure') return { ok: false, problem: 'from_not_a_figure' };
  const high = readBound(to);
  if (high.kind === 'not_a_figure') return { ok: false, problem: 'to_not_a_figure' };
  const min = low.kind === 'figure' ? low.value : null;
  const max = high.kind === 'figure' ? high.value : null;
  if (min !== null && max !== null && min > max) return { ok: false, problem: 'crossed' };
  return { ok: true, min, max };
};

/**
 * What a range box starts with for the figure the draft holds: its plain
 * digits, ungrouped, so the first keystroke edits the number rather than a
 * comma. An open side starts empty.
 */
export const boundTextOf = (value: number | null): string => (value === null ? '' : String(value));

/* ------------------------------------------------------------- the steps */

const NICE_STEPS: readonly number[] = [1, 2, 5];

/**
 * How much one tap moves a range box.
 *
 * Sized off the largest figure the record actually holds, so a shop dealing in
 * lakhs is never offered 1, 2, 3, and a shop counting boxes is never offered
 * 5,000. Roughly a twentieth of the largest, rounded to the nearest 1, 2 or 5
 * times a power of ten.
 */
export const stepFor = (largest: number | null): number => {
  if (largest === null || !Number.isFinite(largest)) return 1;
  // By size, never by sign. A money-out record materializes every `signed_value`
  // negative, so the largest figure on Shop Expenses arrives below zero and the
  // steps there must still be the steps of that shop's real amounts.
  const size = Math.abs(largest);
  if (size <= 20) return 1;
  const target = size / 20;
  const power = Math.floor(Math.log10(target));
  const candidates = [
    ...NICE_STEPS.map((lead) => lead * 10 ** power),
    10 ** (power + 1),
  ];
  let best = candidates[0] ?? 1;
  for (const step of candidates) {
    if (Math.abs(step - target) < Math.abs(best - target)) best = step;
  }
  return best;
};

/* --------------------------------------------------------- the predicate */

/**
 * How a field id becomes SQL, on whichever table the statement reads.
 *
 * Two of these exist and no more: the mirror, where a field is a `json_extract`
 * of `values_json`, and the per-record view, where it is a `f_<fieldId>`
 * column. Passing this in rather than branching inside the builder is what
 * keeps one predicate serving both.
 */
export type FieldColumns = {
  /** The scalar value of one field. */
  readonly valueOf: (fieldId: string) => string;
  /**
   * The table-valued rows of a multi_select field, or null on a source that
   * cannot reach them. The view is such a source: it exposes no `values_json`.
   */
  readonly membersOf: ((fieldId: string) => string) | null;
};

// The view's own spelling, so an index on the view's expression serves the list too.
const jsonPath = jsonPathFor;

/** `entry_mirror`, read directly. What the entry list runs against. */
export const MIRROR_COLUMNS: FieldColumns = {
  valueOf: (fieldId) => `json_extract(values_json, ${jsonPath(fieldId)})`,
  membersOf: (fieldId) => `json_each(values_json, ${jsonPath(fieldId)})`,
};

/**
 * `v_<record>`, where every field already has a column. What a card runs against.
 *
 * A multi_select's column holds the stored array as JSON text, and `json_each`
 * takes a JSON value as readily as it takes a document and a path. So both
 * sources can answer every condition, and the list and the cards can never
 * disagree about which rows a filter keeps.
 */
export const VIEW_COLUMNS: FieldColumns = {
  valueOf: (fieldId) => quoteIdent(columnNameFor(fieldId)),
  membersOf: (fieldId) => `json_each(${quoteIdent(columnNameFor(fieldId))})`,
};

const marks = (count: number): string => new Array(count).fill('?').join(', ');

/**
 * One condition, as a predicate, binding its values.
 *
 * Values are pushed only when a predicate is really produced, so `params` stays
 * in the order the `?` marks appear — the rule `insight-query.ts` sets and this
 * file keeps.
 *
 * A numeric comparison casts, for the same reason the insight compiler casts: a
 * value that arrived as a JSON string would otherwise compare as text, where
 * '9' is greater than '10'.
 */
const fieldPredicate = (
  filter: EntryFieldFilter,
  columns: FieldColumns,
  arrayFields: ReadonlySet<string>,
  params: (string | number)[],
  today: string | null,
): string | null => {
  const column = columns.valueOf(filter.fieldId);

  // A date condition reads the stored day, the first ten characters, with the
  // shared builder the server's evaluator agrees with (F21). No day, no condition.
  if (isDateConditionOp(filter.op)) {
    if (today === null) return null;
    const asked = filter as Extract<EntryFieldFilter, { op: DateConditionOp }>;
    return dateConditionSql(`substr(${column}, 1, 10)`, asked.op, asked.value, today, (day) => {
      params.push(day);
      return '?';
    });
  }

  // An unanswered field is absent from values_json, so json_extract answers
  // NULL. A field cleared to nothing can answer the empty string. Both are
  // "not answered", and both must land on the same side.
  if (filter.op === 'set') return `(${column} IS NOT NULL AND ${column} <> '')`;
  if (filter.op === 'unset') return `(${column} IS NULL OR ${column} = '')`;

  if (filter.op === 'any_of' || filter.op === 'none_of' || filter.op === 'all_of') {
    return membershipPredicate(filter, column, columns.membersOf, arrayFields, params);
  }

  // A numeric comparison casts, for the same reason `aggregateOf` casts in the
  // insight compiler: a value that arrived as a JSON string would otherwise
  // compare as text, where '9' is greater than '10'.
  if (filter.op === 'gte' || filter.op === 'lte') {
    params.push(filter.value);
    return `CAST(${column} AS REAL) ${filter.op === 'gte' ? '>=' : '<='} ?`;
  }

  return null;
};

/** The member of the union that carries a list of values, named so it narrows. */
type ChoiceFilter = Extract<EntryFieldFilter, { op: 'any_of' | 'none_of' | 'all_of' }>;

/**
 * The stored value against a set.
 *
 * Its own function so the union narrows once, at the call, rather than four
 * times inside one long body — the compiler will not carry the narrowing of a
 * discriminant across the branches that follow it here.
 */
const membershipPredicate = (
  filter: ChoiceFilter,
  column: string,
  membersOf: ((fieldId: string) => string) | null,
  arrayFields: ReadonlySet<string>,
  params: (string | number)[],
): string | null => {
  if (filter.values.length === 0) return null;
  const held = marks(filter.values.length);

  if (membersOf !== null && arrayFields.has(filter.fieldId)) {
    // A multi_select stores an array, so membership is a scan of its own rows.
    // An absent key yields no rows, so NOT EXISTS is true for an unanswered
    // field — the same choice `none_of` makes everywhere else.
    params.push(...filter.values);
    if (filter.op === 'all_of') {
      // Every value is among the members: as many distinct matches as values
      // asked. The values are distinct already; a set is what the pane holds.
      params.push(new Set(filter.values).size);
      return `(SELECT COUNT(DISTINCT value) FROM ${membersOf(filter.fieldId)} WHERE value IN (${held})) = ?`;
    }
    const exists = `EXISTS (SELECT 1 FROM ${membersOf(filter.fieldId)} WHERE value IN (${held}))`;
    return filter.op === 'any_of' ? exists : `NOT ${exists}`;
  }

  if (filter.op === 'all_of') {
    // One value cannot be two things: equal to each of the set, which holds
    // only for a set of one.
    params.push(...filter.values);
    return `(${filter.values.map(() => `${column} = ?`).join(' AND ')})`;
  }

  params.push(...filter.values);
  // NULL IN (…) is NULL, so an unanswered field never matches any_of. That is
  // correct: it has no value, so it is not one of these.
  return filter.op === 'any_of'
    ? `${column} IN (${held})`
    : `(${column} IS NULL OR ${column} NOT IN (${held}))`;
};

/**
 * The whole WHERE contributed by the field conditions.
 *
 * Grouped by field in first-seen order. Inside a group the set-family
 * conditions are ORed — "Cash, UPI, or nothing" is one question — and the
 * range pair is ANDed, because a range is two conditions that must both hold.
 * Groups are ANDed with each other.
 */
export const fieldsSql = (
  set: EntryFilterSet,
  columns: FieldColumns,
  arrayFields: ReadonlySet<string>,
  params: (string | number)[],
  /** The day a date condition is judged on. Null: date conditions are left out. */
  today: string | null = null,
): string => {
  const groups: string[] = [];
  for (const fieldId of fieldIdsOf(set)) {
    const anyOf: string[] = [];
    const allOf: string[] = [];
    for (const filter of filtersForField(set, fieldId)) {
      const predicate = fieldPredicate(filter, columns, arrayFields, params, today);
      if (predicate === null) continue;
      (filter.op === 'gte' || filter.op === 'lte' || isDateConditionOp(filter.op) ? allOf : anyOf).push(predicate);
    }
    const parts: string[] = [];
    if (anyOf.length > 0) parts.push(anyOf.length === 1 ? anyOf[0]! : `(${anyOf.join(' OR ')})`);
    if (allOf.length > 0) parts.push(allOf.join(' AND '));
    if (parts.length > 0) groups.push(`(${parts.join(' AND ')})`);
  }
  return groups.length === 0 ? '' : ` AND ${groups.join(' AND ')}`;
};

/* -------------------------------------------------------------- compiler */

export type CompiledEntryFilter = {
  readonly sql: string;
  readonly params: readonly (string | number)[];
};

/**
 * The WHERE every filtered statement shares, and the values it binds.
 *
 * `deleted_at IS NULL` is load-bearing twice over: a tombstone is not an entry,
 * and it is the predicate of the partial index the day range rides.
 */
const whereSql = (
  recordId: string,
  set: EntryFilterSet,
  today: string,
  columns: FieldColumns,
  arrayFields: ReadonlySet<string>,
  params: (string | number)[],
  text: string | null,
): string => {
  params.push(recordId);
  let where = 'WHERE record_id = ? AND is_current = 1 AND deleted_at IS NULL';
  const window = windowOf(set, today);
  if (window !== null) {
    params.push(window.from, window.to);
    where += ' AND day >= ? AND day <= ?';
  }
  return where + fieldsSql(set, columns, arrayFields, params, today) + textSql(text, recordId, params);
};

/**
 * The words, as a clause, or nothing at all.
 *
 * **Last, and only when there is text.** Last because the params array stays in
 * the order the `?` marks appear, and the fields have already pushed theirs.
 * Only when there is text because three `EXPLAIN QUERY PLAN` tests assert the
 * day-range path does not `SCAN entry_mirror`, and a subquery emitted on every
 * call would move the planner off that index for every unfiltered list in the
 * app.
 *
 * **The same two-letter floor global search keeps.** `isSearchable` counts the
 * letters the tokenizer would keep, so three spaces and a comma is not a
 * search; nothing else in this file gates length, and a one-letter MATCH would
 * read the whole index.
 */
const textSql = (text: string | null, recordId: string, params: (string | number)[]): string => {
  if (text === null || !isSearchable(text)) return '';
  const bound = entryTextParams(text, recordId);
  if (bound === null) return '';
  params.push(...bound);
  return ` AND ${entryTextSql()}`;
};

/**
 * The columns `toListRow` reads, aliased so the raw row arrives already
 * shaped. The alias list and that function must change together.
 */
const LIST_COLUMNS = [
  'entry_id AS entryId',
  'version',
  'values_json AS valuesJson',
  'created_at AS createdAt',
  'updated_at AS updatedAt',
  'created_by AS createdBy',
  'source_channel AS sourceChannel',
  'link_id AS linkId',
  'source_entry_id AS sourceEntryId',
  'pending',
].join(', ');

/**
 * The rows, in `listEntries`' exact order.
 *
 * The ORDER BY is copied from it and must stay copied: the list is grouped by
 * day, and a pending row carries no `server_seq` so it sinks to the bottom of
 * its day with `created_at` breaking the tie.
 *
 * @param today       the device's day, `YYYY-MM-DD`, UTC — the same convention
 *                    `dayOf` materializes `entry_mirror.day` with.
 * @param arrayFields the ids of the record's multi_select fields, whose value
 *                    is a stored array rather than a scalar. A filter carries a
 *                    field id and nothing else, so the type has to come in from
 *                    the schema. Empty on every record that ships today.
 */
export const compileEntryFilter = (
  recordId: string,
  set: EntryFilterSet,
  today: string,
  arrayFields: ReadonlySet<string> = new Set(),
  text: string | null = null,
  cut: EntryPageCut = {},
): CompiledEntryFilter => {
  const params: (string | number)[] = [];
  // The cut after the words: the params stay in the order the `?` marks appear.
  let where = whereSql(recordId, set, today, MIRROR_COLUMNS, arrayFields, params, text);
  if (cut.dayAtLeast !== undefined) {
    params.push(cut.dayAtLeast);
    where += ' AND day >= ?';
  }
  let tail = '';
  if (cut.limit !== undefined) {
    params.push(cut.limit);
    tail = ' LIMIT ?';
  }
  return {
    sql:
      `SELECT ${LIST_COLUMNS} FROM entry_mirror ${where} ` +
      `ORDER BY ${LIST_ORDER}${tail}`,
    params,
  };
};

/**
 * How a page of the list is cut. Both absent is the whole list.
 *
 * `dayAtLeast` keeps whole days: a page ends on a day boundary, never inside a
 * day, so a sheet record's day row always counts every mark of its day.
 * `limit` is only for a list with no days to keep whole (see `pageBoundary`).
 */
export type EntryPageCut = { readonly dayAtLeast?: string; readonly limit?: number };

/** The list's order, in one place, so a page and its boundary agree. */
const LIST_ORDER = 'day DESC, server_seq DESC, created_at DESC';

/**
 * The day of the `size`th row of the list, in the list's own order.
 *
 * One row and one column, and no `values_json`: this is what decides where a
 * page ends, and it must cost nothing next to the page itself. No row answered
 * means the whole list fits in `size`; a NULL day means the cut falls among the
 * rows that carry no day, where there are no days to keep whole.
 *
 * `text` is the page's own words, and must be: a boundary counted over rows the
 * words would drop ends the page on the wrong day.
 */
export const compileEntryPageBoundary = (
  recordId: string,
  set: EntryFilterSet,
  today: string,
  size: number,
  arrayFields: ReadonlySet<string> = new Set(),
  text: string | null = null,
): CompiledEntryFilter => {
  const params: (string | number)[] = [];
  const where = whereSql(recordId, set, today, MIRROR_COLUMNS, arrayFields, params, text);
  params.push(size - 1);
  return {
    // By day alone. The rows that share a day tie on it in any order, and the
    // day of the `size`th row is the same whichever way they tie — so the
    // tie-breakers the page needs would only force a sort of the whole record,
    // where `day DESC` walks `entry_mirror_list` and stops at the offset.
    sql: `SELECT day FROM entry_mirror ${where} ORDER BY day DESC LIMIT 1 OFFSET ?`,
    params,
  };
};

/**
 * How many rows matched, and what they come to.
 *
 * The same WHERE as the rows, so the line under the cards cannot disagree with
 * the list below it. `SUM(signed_value)` and not `SUM(amount)`: the sign is
 * already in the materialized column, so a money-out record nets correctly with
 * no knowledge of what the record is for.
 */
export const compileEntryFilterSummary = (
  recordId: string,
  set: EntryFilterSet,
  today: string,
  arrayFields: ReadonlySet<string> = new Set(),
  text: string | null = null,
  movementFieldId: string | null = null,
): CompiledEntryFilter => {
  const params: (string | number)[] = [];
  const where = whereSql(recordId, set, today, MIRROR_COLUMNS, arrayFields, params, text);
  // A count that carries its direction on the value (`measureIsMovement`,
  // R3 of docs/plans/qa-fixes-2.md) has no signed value, so its rows are
  // summed as stored: Stock's -2 is -2, not nothing. The server's catalog
  // headline sums the same quantities (MOVEMENT_TOTAL_SQL in routes/records.ts).
  const net =
    movementFieldId === null
      ? 'SUM(signed_value)'
      : `SUM(CAST(${MIRROR_COLUMNS.valueOf(movementFieldId)} AS REAL))`;
  return {
    sql: `SELECT COUNT(*) AS n, ${net} AS net FROM entry_mirror ${where}`,
    params,
  };
};

/* --------------------------------------------------- the cut for a card */

/**
 * The same filter, compiled for a card rather than for the list.
 *
 * Null when nothing is filtered, so a caller can hand it straight to
 * `compileInsight` and the unfiltered path stays exactly as it was.
 *
 * The predicate is built by the same `fieldPredicate` the list uses, over the
 * view's `f_<fieldId>` columns instead of `json_extract`. That shared builder is
 * the point: a row that passes the list's WHERE passes this one, so the number
 * over the list can never describe a different set of rows than the list.
 *
 * The leading ` AND ` the list form carries is trimmed, because
 * `compileInsight` wraps this in its own parentheses.
 */
export const insightCutOf = (
  set: EntryFilterSet,
  today: string,
  arrayFields: ReadonlySet<string> = new Set(),
): RuntimeCut | null => {
  if (!isFiltering(set)) return null;
  const params: (string | number)[] = [];
  const clause = fieldsSql(set, VIEW_COLUMNS, arrayFields, params, today).replace(/^ AND /, '');
  return {
    window: windowOf(set, today),
    predicate: clause === '' ? null : { sql: clause, params },
  };
};
